import { ReportParameterModel, ReportDynamicParametersModel } from 'app/models/dms/reportParameter.model';
import { ReportActionRequestModel, ReportSpecModel } from 'app/models/dms/reportSpec.model';
import { UserService } from 'app/services/user.service';

import { ReportParamType, ReportSpecType, ReportType, SecurityRight, VehicleClass} from 'app/utility/enums';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { CacheService } from './cache.service';
import { Constants } from 'app/utility/constants';

import { Observable } from 'rxjs';
import { LabelValueModel } from 'app/models/shared/labelValue.model';
import { FieldGroupModel } from 'app/models/shared/fieldGroup.model';
import { FieldModel } from 'app/models/shared/field.model';
import { TabModel } from 'app/models/shared/tab.model';


@Injectable({
	providedIn: 'root'
})
export class ReportService
{

	constructor(
        private httpClient: HttpClient, 
        private cache: CacheService, 
        private userService: UserService,
    ) { }

	getCachedReportSpecs(reportSpecType: ReportSpecType, refresh:boolean = false): Observable<ReportSpecModel[]>
	{
		let cacheKey: string = 'reportspecs-' + reportSpecType.toString();
		let cacheMilliseconds: number = 1 * 60 * 600001; //1 hour
		if (refresh)
		{
			this.cache.clear(cacheKey);
		}
        if (this.userService.mcMinVersion("3.2.22"))
        {
            return this.getReportDynamicSpecs(reportSpecType);
            // return this.cache.get(cacheKey, this.getReportDynamicSpecs(), cacheMilliseconds);
        }
        else
        {
            return this.cache.get(cacheKey, this.getReportSpecs(), cacheMilliseconds);
        }
	}

	getReportSpecs(): Observable<ReportSpecModel[]>
	{
		let httpParams = new HttpParams();
		return this.httpClient.get<ReportSpecModel[]>(Constants.ApiUrl + 'Reports/Specs', { params: httpParams });
	}

    getReportDynamicSpecs(reportSpecType: ReportSpecType): Observable<ReportSpecModel[]>
	{
		let httpParams = new HttpParams();
        httpParams = httpParams.set("reportSpecType", reportSpecType.toString());
		return this.httpClient.get<ReportSpecModel[]>(Constants.ApiUrl + 'Reports/DynamicSpecs', { params: httpParams });
	}

	getCachedReportSpec(id: string): Observable<ReportSpecModel>
	{
		let cacheKey: string = 'reportspec' + id;
		let cacheMilliseconds: number = 1 * 60 * 60001; //1 hour
		return this.cache.get(cacheKey, this.getReportSpec(id), cacheMilliseconds);
	}

	getReportSpec(id: string): Observable<ReportSpecModel>
	{
		let httpParams = new HttpParams();
		return this.httpClient.get<ReportSpecModel>(Constants.ApiUrl + 'Reports/Spec/' + id, { params: httpParams });
	}

    getCachedReportDynamicSpec(id: string, clearCache: boolean = false): Observable<ReportSpecModel>
    {
        let cacheKey: string = 'dynamicreportspec' + id;
        let cacheMilliseconds: number = 1 * 60 * 60001; //1 hour
        if (clearCache)
        {
            this.cache.clear(cacheKey);
        }
        return this.cache.get(cacheKey, this.getReportDynamicSpec(id), cacheMilliseconds);
    }

    getReportDynamicSpec(id: string): Observable<ReportSpecModel>
	{
		let httpParams = new HttpParams();
		return this.httpClient.get<ReportSpecModel>(Constants.ApiUrl + 'Reports/DynamicSpec/' + id, { params: httpParams });
	}

	getReport(reportType: string, params: ReportParameterModel[])
	{
		let httpParams = new HttpParams();
		params.forEach(function (item)
		{
			httpParams = httpParams.set(item.name, item.value);
		});
		// console.log(params, httpParams);
		return this.httpClient.get<any>(Constants.ApiUrl + 'Reports/' + reportType, { params: httpParams });
	}

	getReportPdf(reportType: string, params: ReportParameterModel[]): Observable<Blob>
	{
		let httpParams = new HttpParams();
		params.forEach(function (item)
		{
			httpParams = httpParams.set(item.name, item.value);
		});
		// console.log(params, httpParams);
		return this.httpClient.get(Constants.ApiUrl + 'Reports/' + reportType + "/pdf", { params: httpParams, responseType: "blob" });
	}

	getReportUrl(reportType: string, params: ReportParameterModel[]): Observable<LabelValueModel>
	{
		let httpParams = new HttpParams();
		params.forEach(function (item)
		{
			httpParams = httpParams.set(item.name, item.value);
		});
		// console.log(params, httpParams);
		return this.httpClient.get<LabelValueModel>(Constants.ApiUrl + 'Reports/' + reportType + "/url", { params: httpParams });
	}

    runReport(paramPayload: ReportDynamicParametersModel): Observable<string> 
    {
        return this.httpClient.post<string>(Constants.ApiUrl + 'Reports/RunReport', paramPayload);
    }

	getReportForObject(reportType: ReportType, id: string): Observable<Blob>
	{
		let httpParams = new HttpParams();
		return this.httpClient.get(Constants.ApiUrl + 'Reports/' + reportType + "/" + id + "/pdf", { params: httpParams, responseType: "blob" });
	}

	getReportUrlForObject(reportType: ReportType, id: string): Observable<LabelValueModel>
	{
		let httpParams = new HttpParams();
		return this.httpClient.get<LabelValueModel>(Constants.ApiUrl + 'Reports/' + reportType + "/" + id + "/url", { params: httpParams });
	}

    getCachedFavouriteReportIds(refresh:boolean = false): Observable<string[]>
    {
        let cacheKey: string = 'favouritereports';
        let cacheMilliseconds: number = 1 * 60 * 60001; //1 hour
        if (refresh)
        {
            this.cache.clear(cacheKey);
        }
        return this.cache.get(cacheKey, this.getFavouriteReportIds(), cacheMilliseconds);
    }

    getFavouriteReportIds(): Observable<string[]>
    {
        return this.httpClient.get<string[]>(Constants.ApiUrl + 'Reports/Favourites');
    }

    toggleFavouriteReportId(reportSpecId: string): Observable<boolean>
    {
        return this.httpClient.post<boolean>(Constants.ApiUrl + 'Reports/' + reportSpecId + '/ToggleFavourite', null);
    }

    public async setupFieldGroups(reportSpec: ReportSpecModel, isExpress: boolean, isSale: boolean = false, editActive: boolean = false, makeDirty: boolean = false, disableClone: boolean = false): Promise<TabModel[]>
    {

        var fieldGroupTabs: TabModel[] = [];

        if (this.userService.hasUserRight(SecurityRight.Reports))
        {
            fieldGroupTabs.push(new TabModel("General", reportSpec, await this.getFieldGroups(reportSpec, "General"), makeDirty, "general", "", this.userService.hasUserRight(SecurityRight.Reports), this.userService.hasUserRight(SecurityRight.Reports)));
        }

        return fieldGroupTabs;
    }
    
    public async getFieldGroups(reportSpec: ReportSpecModel, groupId: string, isExpress: boolean = false): Promise<FieldGroupModel[]> {
        var field_groups = [];
        var group: FieldGroupModel;
        switch (groupId)
        {
            case "General":
                group = new FieldGroupModel("Report Parameters");

                for (const param of reportSpec.reportParamSets)
                {
                    param.paramId = param.reference && param.reference.length > 0 ? param.reference.charAt(0).toLowerCase() + param.reference.slice(1) : param.reference;
                    param.paramLabel = param.reference.replace(/(?<!^)([A-Z])/g, ' $1');
                    if (param.paramLabelOverride && param.paramLabelOverride.length > 0) param.paramLabel = param.paramLabelOverride;

                    switch (param.type)
                    {
                        case ReportParamType.Guid:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "contact", param.paramId, true, false, param.reportParamObject));
                            break;
                        case ReportParamType.Select:
                        case ReportParamType.Enum:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "select", param.paramId, true, false, param.reportParamObject));
                            break;
                        case ReportParamType.Selects:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "selects", param.paramId, true, false, param.reportParamObject));
                            break;
                        case ReportParamType.Boolean:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "toggle", param.paramId, true, false));
                            break;
                        case ReportParamType.String:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "text", param.paramId, true, false));
                            break;
                        case ReportParamType.Date:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "date", param.paramId, true, false));
                            break;
                        case ReportParamType.DateRange:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "date-picker", param.paramId, true, false, param.reportParamObject));
                            break;
                        case ReportParamType.Integer:
                            var integer = new FieldModel(param.paramLabel, param.reportParamValue, "number", param.paramId, true, false);
                            if (param.reportParamValue == null)
                            {
                                integer.allowZero = false;
                            }
                            group.addField(integer);
                            break;
                        case ReportParamType.Lookup:
                            var lookup = new FieldModel(param.paramLabel, param.reportParamValue, "lookup", param.paramId, true, false, param.reportParamObject);
                            lookup.noneLabel = param.noneLabel;
                            lookup.includeNone = param.noneLabel && param.noneLabel.length > 0;
                            group.addField(lookup);
                            break;
                        case ReportParamType.Lookups:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "lookups", param.paramId, true, false, param.reportParamObject, null, VehicleClass.All));
                            break;
                        default:
                            group.addField(new FieldModel(param.paramLabel, param.reportParamValue, "text", param.paramId, false, false, null, null, null, true));
                            break;
                    }
                }

                field_groups.push(group);
                break;

        }
        return field_groups;
    }

    public setupListFieldGroups(reportSpecs: ReportSpecModel[], isExpress: boolean, isSale: boolean = false, editActive: boolean = false, makeDirty: boolean = false, disableClone: boolean = false, favouriteReportIds: string[] = null): TabModel[]
    {
        var fieldGroupTabs: TabModel[] = [];

        if (this.userService.mcMinVersion("3.2.23") && favouriteReportIds != null && favouriteReportIds.length > 0)
        {
            const groups = this.getListFieldGroups(reportSpecs, "Favourites", favouriteReportIds);
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Favourites", reportSpecs, groups, makeDirty, "favourites", "", this.userService.mcMinVersion("3.2.23"), this.userService.mcMinVersion("3.2.23")));
            }
        }


        if (this.userService.hasUserRight(SecurityRight.ReportsStock))
        {
            const groups = this.getListFieldGroups(reportSpecs, "Stock");
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Stock", reportSpecs, groups, makeDirty, "stock", "", this.userService.hasUserRight(SecurityRight.ReportsStock), this.userService.hasUserRight(SecurityRight.ReportsStock)));
            }
        }

        if (this.userService.hasUserRight(SecurityRight.ReportsSales))
        {
            const groups = this.getListFieldGroups(reportSpecs, "Sales");
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Sales", reportSpecs, groups, makeDirty, "sales", "", this.userService.hasUserRight(SecurityRight.ReportsSales), this.userService.hasUserRight(SecurityRight.ReportsSales)));
            }
        }

        if (this.userService.hasUserRight(SecurityRight.ReportsPurchases))
        {
            const groups = this.getListFieldGroups(reportSpecs, "Purchases");
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Purchases", reportSpecs, groups, makeDirty, "purchases", "", this.userService.hasUserRight(SecurityRight.ReportsPurchases), this.userService.hasUserRight(SecurityRight.ReportsPurchases)));
            }
        }

        if (this.userService.hasUserRight(SecurityRight.ReportsFinancials))
        {
            const groups = this.getListFieldGroups(reportSpecs, "Financial");
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Financials", reportSpecs, groups, makeDirty, "financial", "", this.userService.hasUserRight(SecurityRight.ReportsFinancials), this.userService.hasUserRight(SecurityRight.ReportsFinancials)));
            }
        }

        if (this.userService.hasUserRight(SecurityRight.ReportsAdmin))
        {
            const groups = this.getListFieldGroups(reportSpecs, "Admin");
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Admin", reportSpecs, groups, makeDirty, "admin", "", this.userService.hasUserRight(SecurityRight.ReportsAdmin), this.userService.hasUserRight(SecurityRight.ReportsAdmin)));
            }
        }

        if (this.userService.hasUserRight(SecurityRight.ReportsCustom))
        {
            const groups = this.getListFieldGroups(reportSpecs, "Custom");
            if (groups.length > 0)
            {
                fieldGroupTabs.push(new TabModel("Custom", reportSpecs, groups, makeDirty, "custom", "", this.userService.hasUserRight(SecurityRight.ReportsCustom), this.userService.hasUserRight(SecurityRight.ReportsCustom)));
            }
        }

        return fieldGroupTabs;
    }

    public getListFieldGroups(reportSpecs: ReportSpecModel[], groupId: string, favouriteReportIds: string[] = null): FieldGroupModel[]
    {
        var field_groups = [];
        var group: FieldGroupModel;
        switch (groupId)
        {
            case "Favourites":
                group = new FieldGroupModel("Favourites");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (favouriteReportIds.indexOf(reportSpec.reportSpecId) >= 0)
                    {
                        var field = new FieldModel("Favourites", reportSpec.name, "currency", "favourite", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });

                field_groups.push(group);
                break;
            case "Stock":
                group = new FieldGroupModel("Stock");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (reportSpec.specType == ReportSpecType.StockGroup)
                    {
                        var field = new FieldModel("Stock", reportSpec.name, "currency", "stock", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });

                field_groups.push(group);
                break;
            case "Sales":
                group = new FieldGroupModel("Sales");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (reportSpec.specType == ReportSpecType.SalesGroup)
                    {
                        var field = new FieldModel("Sales", reportSpec.name, "currency", "sales", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });
                field_groups.push(group);
                break;
            case "Purchases":
                group = new FieldGroupModel("Purchases");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (reportSpec.specType == ReportSpecType.PurchasesGroup)
                    {
                        var field = new FieldModel("Purchases", reportSpec.name, "currency", "purchases", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });
                field_groups.push(group);
                break;
            case "Financial":
                group = new FieldGroupModel("Financial");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (reportSpec.specType == ReportSpecType.FinancialsGroup)
                    {
                        var field = new FieldModel("Financial", reportSpec.name, "currency", "financial", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });
                field_groups.push(group);
                break;
            case "Admin":
                group = new FieldGroupModel("Admin");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (reportSpec.specType == ReportSpecType.AdminGroup)
                    {
                        var field = new FieldModel("Admin", reportSpec.name, "currency", "admin", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });
                field_groups.push(group);
                break;
            case "Custom":
                group = new FieldGroupModel("Custom");
                reportSpecs.forEach(function (reportSpec)
                {
                    if (reportSpec.specType == ReportSpecType.CustomGroup)
                    {
                        var field = new FieldModel("Custom", reportSpec.name, "currency", "custom", false, false);
                        field.label = reportSpec.shortDesc;
                        group.addField(field);
                    }
                });
                field_groups.push(group);
                break;

        }

        if (group && group.fields && group.fields.length == 0)
        {
            field_groups = [];
            
        }

        return field_groups;
    }

    public setupReportListFieldGroups(reportSpecs: ReportSpecModel[], lastSelectedId: string, favouriteReportIds: string[] = null): any[]
    {
        let field_groups = [];

        let favouriteGroup = new FieldGroupModel("", "Favourites");
        let stockGroup = new FieldGroupModel("", "Stock");
        let salesGroup = new FieldGroupModel("", "Sales");
        let purchasesGroup = new FieldGroupModel("", "Purchases");
        let financialsGroup = new FieldGroupModel("", "Financials");
        let adminGroup = new FieldGroupModel("", "Admin");
        let customGroup = new FieldGroupModel("", "Custom");

        if (reportSpecs && reportSpecs.length > 0)
        {
            for (let reportSpec of reportSpecs)
            {

                //create a variable of payment amount as a currency
                var reportDetails = new FieldModel(reportSpec.name, " ");
                reportDetails.id = reportSpec.reportSpecId;
                reportDetails.sublabel = reportSpec.shortDesc || '-';
                reportDetails.detailObject = reportSpec;
                reportDetails.detailObject.type = "reportSpec";
                reportDetails.classes = 'vehicle-activity-list-item';
                if (reportDetails.id == lastSelectedId) reportDetails.selected = true;
                else reportDetails.selected = false;
                reportDetails.detail = true;
                if (this.userService.mcMinVersion("3.2.23"))
                {
                    reportDetails.hasButton = true;
                    reportDetails.buttonIsEndSlot = true;
                    reportDetails.buttonIonIcon = "star-outline";
                    reportDetails.buttonName = "button-favourite-report";
    
                    if (favouriteReportIds && favouriteReportIds.length > 0 && favouriteReportIds.indexOf(reportSpec.reportSpecId) >= 0)
                    {   
                        reportDetails.buttonIonIcon = "star";
                        reportDetails.classes += " favourite";
                        favouriteGroup.addField(reportDetails);
                    }
                }

                switch (reportSpec.specType)
                {
                    case ReportSpecType.StockGroup:
                        
                        stockGroup.addField(reportDetails);
                        break;
                    case ReportSpecType.SalesGroup:
                        salesGroup.addField(reportDetails);
                        break;
                    case ReportSpecType.PurchasesGroup:
                        purchasesGroup.addField(reportDetails);
                        break;
                    case ReportSpecType.FinancialsGroup:
                        financialsGroup.addField(reportDetails);
                        break;
                    case ReportSpecType.AdminGroup:
                        adminGroup.addField(reportDetails);
                        break;
                    case ReportSpecType.CustomGroup:
                        customGroup.addField(reportDetails);
                        break;
                }

            }
        }

        favouriteGroup.singleColumn = true;
        stockGroup.singleColumn = true;
        salesGroup.singleColumn = true;
        purchasesGroup.singleColumn = true;
        financialsGroup.singleColumn = true;
        adminGroup.singleColumn = true;
        customGroup.singleColumn = true;

        field_groups.push(favouriteGroup);
        field_groups.push(stockGroup);
        field_groups.push(salesGroup);
        field_groups.push(purchasesGroup);
        field_groups.push(financialsGroup);
        field_groups.push(adminGroup);
        field_groups.push(customGroup);

        return field_groups;
    }

    performReportAction(reportActionPayload: ReportActionRequestModel): Observable<string>
    {
        return this.httpClient.post<string>(Constants.ApiUrl + 'Reports/PerformReportAction', reportActionPayload);        
    }

}
